import { Controller, useForm } from "react-hook-form"
import { Navigate, useNavigate } from "react-router"
import { Spacer } from "../../../../../components/spacer/Spacer"
import { Span } from "../../../../../components/span/Span"
import { theme } from "../../../../../styles/stitches.config"
import { getFormError, getFormSuccess } from "../../../../../utils/form"
import { SResponsiveRow } from "../../acquisitionForm/AcquisitionForm.styled"
import { CenterWrapper } from "../components/CenterWrapper"
import { NavigationButtons } from "../components/NavigationButtons"
import { Subheader } from "../components/Subheader"
import { FormItem, FormLabel } from "../components/Form"
import { FormInput, InputDisclaimer } from "../components/Input"
import { AddressFormSection } from "./AddressFormSection"
import { GDPRLink } from "./GdprLink"
import {
  BusinessContactFormVales,
  businessContactFormSchema,
  useStoredBusinessContactFormValues,
} from "./businessContactForm.utils"
import { ACQUISITION_FORM_LINKS } from "../links"
import { zodResolver } from "@hookform/resolvers/zod"
import Text from "../../../../../components/Text"
import { CountrySelect } from "../components/CountrySelect"
import { Checkbox, CheckboxedSection } from "../components/Checkbox"
import { DateInputBlock } from "../components/DateInputBlock"
import { FormHeader } from "../components/FormHeader"
import { useAcquisitionProgress } from "../AcquisitionProgressContext"

export function BusinessContactForm() {
  const [contactInfo, storeContactInfo] = useStoredBusinessContactFormValues()
  const navigate = useNavigate()
  const progress = useAcquisitionProgress()

  const form = useForm<BusinessContactFormVales>({
    resolver: zodResolver(businessContactFormSchema),
    mode: "onBlur",
    defaultValues: {
      addressStreet: "",
      addressCity: "",
      addressOrientationNumber: "",
      addressPostalCode: "",
      addressCountryCode: "CZ",
      contactEmail: "",
      contactFirstName: "",
      contactLastName: "",
      contactPhone: "",
      gdprConsent: false,
      companyName: "",
      registrationNumber: "",
      taxNumber: "",
      ...contactInfo,
    },
  })

  const contactAddressWatch = form.watch("contactAddress")
  const deliveryPointAddressWatch = form.watch("deliveryPointAddress")

  function handleReturn() {
    navigate(`..${progress.previousStepUrl}`)
  }

  function handleSumbit(data: BusinessContactFormVales) {
    storeContactInfo(data)
    navigate(`..${progress.nextStepUrl}`)
  }

  if (!progress.contractInfo) {
    return <Navigate to={`..${ACQUISITION_FORM_LINKS.CONTRACT_TYPE("business")}`} />
  }

  return (
    <CenterWrapper>
      <FormHeader>Osobní údaje</FormHeader>
      <Spacer size={theme.space.s6} />

      <Text type="headerH2" css={{ textAlign: "center", fontSize: theme.sizes.s5, display: "block" }}>
        Kontaktní osoba
      </Text>

      <Spacer size={theme.space.s6} />
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form onSubmit={form.handleSubmit(handleSumbit)}>
        <SResponsiveRow>
          <Controller
            control={form.control}
            name="contactFirstName"
            render={({ field }) => (
              <FormItem css={{ width: "100%" }}>
                <FormLabel htmlFor="contactFirstName">Jméno</FormLabel>
                <FormInput
                  id="contactFirstName"
                  type="text"
                  placeholder="např. Petr"
                  width="100%"
                  {...field}
                  error={getFormError({ form: form, name: "contactFirstName" })}
                  success={getFormSuccess({ form: form, name: "contactFirstName" })}
                />
              </FormItem>
            )}
          />

          <Controller
            control={form.control}
            name="contactLastName"
            render={({ field }) => (
              <FormItem css={{ width: "100%" }}>
                <FormLabel htmlFor="contactLastName">Příjmení</FormLabel>
                <FormInput
                  id="contactLastName"
                  type="text"
                  placeholder="např. Novák"
                  width="100%"
                  {...field}
                  error={getFormError({ form: form, name: "contactLastName" })}
                  success={getFormSuccess({ form: form, name: "contactLastName" })}
                />
              </FormItem>
            )}
          />
        </SResponsiveRow>

        <Spacer size={theme.space.s8} />

        <SResponsiveRow>
          <Controller
            control={form.control}
            name="contactEmail"
            render={({ field }) => (
              <FormItem css={{ width: "100%" }}>
                <FormLabel>E-mail</FormLabel>
                <FormInput
                  id="contactEmail"
                  type="email"
                  placeholder="vasemail@email.com"
                  width="100%"
                  {...field}
                  error={getFormError({ form, name: "contactEmail" })}
                  success={getFormSuccess({ form, name: "contactEmail" })}
                />
              </FormItem>
            )}
          />

          <Controller
            control={form.control}
            name="contactPhone"
            render={({ field }) => (
              <FormItem css={{ width: "100%" }}>
                <FormLabel>Telefon</FormLabel>
                <FormInput
                  id="contactPhone"
                  type="tel"
                  placeholder="např. 734308996"
                  width="100%"
                  {...field}
                  error={getFormError({ form, name: "contactPhone" })}
                  success={getFormSuccess({ form, name: "contactPhone" })}
                />
              </FormItem>
            )}
          />
        </SResponsiveRow>

        <Spacer size={theme.space.s6} />

        <DateInputBlock
          form={form}
          dateFieldName="contactBirthDate"
          monthFieldName="contactBirthMonth"
          yearFieldName="contactBirthYear"
          label="Datum narození"
        />

        <Spacer size={theme.space.s3_5} />

        <InputDisclaimer label="Pro návrh smlouvy potřebujeme znát vaše datum narození" />

        <Spacer size={theme.space.s9} />
        <Subheader asSectionHeader>Údaje o firmě</Subheader>
        <Spacer size={theme.space.s6} />
        <Controller
          control={form.control}
          name="companyName"
          render={({ field }) => (
            <FormItem css={{ width: "100%" }}>
              <FormLabel htmlFor="rucne-6-companyName">Název firmy</FormLabel>
              <FormInput
                id="companyName"
                type="text"
                placeholder="např. Company s.r.o."
                {...field}
                error={getFormError({ form, name: "companyName" })}
                success={getFormSuccess({ form, name: "companyName" })}
              />
            </FormItem>
          )}
        />

        <Spacer size={theme.space.s9} />
        <SResponsiveRow>
          <Controller
            control={form.control}
            name="registrationNumber"
            render={({ field }) => (
              <FormItem css={{ width: "100%" }}>
                <FormLabel htmlFor="rucne-6-ico">IČO</FormLabel>
                <FormInput
                  id="registrationNumber"
                  type="text"
                  placeholder="Osm číslic"
                  {...field}
                  error={getFormError({ form, name: "registrationNumber" })}
                  success={getFormSuccess({ form, name: "registrationNumber" })}
                />
              </FormItem>
            )}
          />

          <Controller
            control={form.control}
            name="taxNumber"
            render={({ field }) => (
              <FormItem css={{ width: "100%" }}>
                <FormLabel htmlFor="taxNumber">
                  DIČ <Span css={{ color: theme.colors.textsAlt }}>(nepovinné)</Span>
                </FormLabel>
                <FormInput
                  id="taxNumber"
                  type="text"
                  placeholder="CZ + osm až 10 číslic"
                  {...field}
                  error={getFormError({ form, name: "taxNumber" })}
                  success={getFormSuccess({ form, name: "taxNumber" })}
                />
              </FormItem>
            )}
          />
        </SResponsiveRow>

        <Spacer size={theme.space.s10} />

        <Subheader asSectionHeader>Adresa sídla firmy</Subheader>

        <Spacer size={theme.space.s6} />

        <AddressFormSection
          form={form}
          cityId="addressCity"
          streetId="addressStreet"
          descriptiveNumberId="addressDescriptiveNumber"
          orientationNumberId="addressOrientationNumber"
          postalCodeId="addressPostalCode"
        />

        <Spacer size={theme.space.s6} />
        <Controller
          control={form.control}
          name="addressCountryCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Země</FormLabel>
              <CountrySelect
                id="addressCountryCode"
                {...field}
                error={getFormError({ form, name: "addressCountryCode" })}
                success={getFormSuccess({ form, name: "addressCountryCode" })}
              />
            </FormItem>
          )}
        />

        <Spacer size={theme.space.s6} />

        <CheckboxedSection
          checkboxLabel="Odběrné místo mám na jiné adrese než trvalé bydliště"
          onCollapse={() => {
            form.resetField("deliveryPointAddress")
            form.setValue("deliveryPointAddress", null)
          }}
          open={!!deliveryPointAddressWatch}
          hiddenContent={
            <>
              <Subheader asSectionHeader>Adresa odběrného místa</Subheader>
              <Spacer size={theme.space.s5_5} />
              <AddressFormSection
                form={form}
                cityId="deliveryPointAddress.city"
                streetId="deliveryPointAddress.street"
                descriptiveNumberId="deliveryPointAddress.descriptiveNumber"
                orientationNumberId="deliveryPointAddress.orientationNumber"
                postalCodeId="deliveryPointAddress.postalCode"
              />
            </>
          }
        />

        <Spacer size={theme.space.s6} />

        <CheckboxedSection
          checkboxLabel={`Kontaktní adresu mám na jiné adrese než trvalé bydliště`}
          onCollapse={() => {
            form.resetField("contactAddress")
            form.setValue("contactAddress", null)
          }}
          open={!!contactAddressWatch}
          hiddenContent={
            <>
              <Subheader asSectionHeader>Kontaktní adresa</Subheader>
              <Spacer size={theme.space.s5_5} />
              <AddressFormSection
                form={form}
                cityId="contactAddress.city"
                streetId="contactAddress.street"
                descriptiveNumberId="contactAddress.descriptiveNumber"
                orientationNumberId="contactAddress.orientationNumber"
                postalCodeId="contactAddress.postalCode"
              />
              <Spacer size={theme.space.s4} />

              <Controller
                control={form.control}
                name="contactAddress.countryCode"
                defaultValue="CZ"
                render={({ field }) => (
                  <FormItem>
                    <CountrySelect
                      id="contactAddress.countryCode"
                      {...field}
                      value={field.value}
                      error={getFormError({ form, name: "contactAddress.countryCode" })}
                      success={getFormSuccess({ form, name: "contactAddress.countryCode" })}
                    />
                  </FormItem>
                )}
              />
            </>
          }
        />

        <Spacer size={theme.space.s9} />
        <Controller
          control={form.control}
          name="gdprConsent"
          render={({ field, fieldState }) => (
            <Checkbox
              {...field}
              align="start"
              error={fieldState.error?.message}
              errorMsgCSS={{ paddingLeft: theme.space.s12_5 }}
              label={
                <>
                  <Span css={{ maxWidth: "425px", display: "block" }}>
                    Souhlasím se zpracováním svých osobních údajů, více informací <GDPRLink>zde</GDPRLink>.
                  </Span>
                </>
              }
            />
          )}
        />

        <Spacer size={theme.space.s10} />

        <Spacer size={theme.space.s6} />

        <NavigationButtons
          prevText="Výběr typu smlouvy"
          nextText={progress.contractInfo.product === "production" ? "Výrobní místo" : "Odběrné místo"}
          onReturn={handleReturn}
          isSubmitting={form.formState.isSubmitting}
        />
      </form>
      <Spacer size={theme.space.s6} />
    </CenterWrapper>
  )
}
